import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldError } from 'react-hook-form';
import { translateFieldError } from '@/lib/errors';

/**
 * `fieldState.error.message` is polymorphic: the zod resolver stores a raw
 * ErrorCode i18n key, while `setFormValidationErrors`/`applyApiErrorToForm`
 * store an already-translated sentence. `translateFieldError` is the one place
 * that tells them apart — a naive `t()` would namespace-split a translated
 * sentence on its first colon, because i18next's default `nsSeparator` is ':'.
 *
 * Returned as a mapper rather than a value because adapters need it inside
 * `Controller`'s render callback, where hooks cannot be called.
 */
export function useFieldErrorMessage(): (error?: FieldError) => string | undefined {
  const { t } = useTranslation();

  return useCallback(
    (error?: FieldError) => (error?.message ? translateFieldError(error.message, t) : undefined),
    [t]
  );
}
